import React, { useCallback, useMemo } from 'react';
import {
  Form,
  Tabs,
  Row,
  Col,
  Select,
} from 'antd';
import { useSelector } from 'react-redux';

import OnTraccrTextInput from '../../inputs/OnTraccrTextInput';

import {
  AUTO_COLLECTED_ID,
} from '../../../forms/forms.constants';

import PDFDesignerLinkPicker from './PDFDesignerLinkPicker';
import PDFDesignerPositionPicker from './PDFDesignerPositionPicker';
import PDFDesignerColorPicker from './PDFDesignerColorPicker';
import PDFDesignerBorderPicker from './PDFDesignerBorderPicker';
import PDFDesignerPagePicker from './PDFDesignerPagePicker';
import PDFDesignerFontPicker from './PDFDesignerFontPicker';
import PDFRichTextEditor from './PDFRichTextEditor';

import { defaultDrawOptions } from './PDFDesigner.constants';
import OnTraccrCheckbox from '../../inputs/OnTraccrCheckbox';
import HoverHelp from '../../HoverHelp';
import { getPopupContainer } from '../pdfHelpers';
import { isFieldLinked } from './PDFDesignerHelpers';

const formStyle = { marginBottom: 5 };

const hideIfEmptySelectorHelp = (
  <div style={{ width: 280 }}>
    Choose a form field that has &quot;Hide in PDF if empty&quot; enabled. This text
    element is omitted from the PDF when that field has no entered data.
  </div>
);

const selectValueToHideIfEmptyLink = (value) => {
  if (!value) return null;
  const separatorIndex = value.indexOf(':');
  if (separatorIndex < 0) return null;
  const sectionId = value.slice(0, separatorIndex);
  const fieldId = value.slice(separatorIndex + 1);
  return sectionId && fieldId ? { sectionId, fieldId } : null;
};

export default function PDFTextFieldConfigure({
  onDrawOptionChanged,
  page,
  numberOfPages,
  onPageChanged,
  onTextChanged,
  onApplyTextStyles,
  onLinkChanged,
  onIsSectionHeaderChanged,
  field:{
    text,
    drawOptions = defaultDrawOptions,
    link = {},
    isHeader,
    isSectionHeader,
    tags = [],
  } = {},
  sections = [],
  collected = {},
  typeId,
  useStandardTemplate,
  useV3,
  lockedRepeatableLayoutSectionId,
}) {
  const {
    backgroundColor,
    linkTextStyle = {},
    hideIfEmptyLink,
  } = drawOptions;

  const {
    sectionId: emptyLinkSectionId,
    fieldId: emptyLinkFieldId,
  } = hideIfEmptyLink ?? {};

  const {
    [typeId]: anchorNumber,
  } = useSelector((state) => {
    const {
      settings: {
        company: {
          settings: {
            formAnchors = {},
          } = {},
        } = {},
      } = {},
    } = state;
    return formAnchors;
  });

  const hideIfEmptyFieldOptions = useMemo(() => {
    const options = [];
    sections.forEach(({ id: sectionId, name: sectionName, fields = [] }) => {
      fields.forEach((field) => {
        const { id: fieldId, configProps: { title, hideIfEmptyInPDF } = {} } = field;
        if (!hideIfEmptyInPDF) return;
        const fieldTitle = (title || '').trim() || fieldId;
        options.push({
          value: `${sectionId}:${fieldId}`,
          label: `${sectionName} - ${fieldTitle}`,
        });
      });
    });
    return options;
  }, [sections]);

  const hideIfEmptySelectValue = emptyLinkSectionId && emptyLinkFieldId ? `${emptyLinkSectionId}:${emptyLinkFieldId}` : undefined;

  const onChange = useCallback((e) => onTextChanged(e.target.value), [onTextChanged]);

  const onRichTextChange = useCallback((richText) => {
    onTextChanged(richText);
  }, [onTextChanged]);

  const handleLinkChange = useCallback((newLink) => {
    const fullLink = {
      ...link,
      ...newLink,
    };
    const { sectionId, fieldId } = fullLink;
    if (sectionId === AUTO_COLLECTED_ID
      && fieldId === 'number'
      && text === 'text'
      && anchorNumber) {
      // Show preview of number
      onTextChanged(anchorNumber.toString());
    }
    if (isFieldLinked(fullLink) && hideIfEmptyLink) {
      onDrawOptionChanged({ hideIfEmptyLink: null });
    }
    onLinkChanged(newLink);
  }, [text, link, onLinkChanged, anchorNumber, hideIfEmptyLink, onDrawOptionChanged]);

  const onHideIfEmptyFieldChange = useCallback((value) => {
    onDrawOptionChanged({
      hideIfEmptyLink: selectValueToHideIfEmptyLink(value),
    });
  }, [onDrawOptionChanged]);

  const showHideIfEmptySelector = useV3 && !useStandardTemplate && !isFieldLinked(link);

  return (
    <Form layout='vertical' style={{ marginTop: 10, width:'100%' }}>
      <Tabs>
        <Tabs.TabPane tab="General" key="general">
          <Form.Item
            key="value"
            style={formStyle}
            label={useV3 ? (
              <Row gutter={10}>
                <Col>Value</Col>
                <Col>
                  <HoverHelp
                    content={(
                      <div style={{ width: 300 }}>
                        Use # to insert form values into the text. Use the toolbar to format text with bold, italic, or underline.
                        If the field is linked to a form field (section and field selected below), the selected styles apply to the whole text.
                      </div>
                    )}
                  />
                </Col>
              </Row>
            ) : 'Value'}
          >
            {useV3 ? (
              <PDFRichTextEditor
                value={text}
                onChange={onRichTextChange}
                sections={sections}
                collected={collected}
                tags={tags}
                link={link}
                linkTextStyle={linkTextStyle}
                onApplyTextStyles={onApplyTextStyles}
              />
            ) : (
              <OnTraccrTextInput onChange={onChange} defaultValue={text} textarea/>
            )}
          </Form.Item>
          <Form.Item key="isSectionHeader" style={formStyle}>
            <OnTraccrCheckbox
              value={isSectionHeader}
              onChange={onIsSectionHeaderChanged}
              label="Section Header"
              style={{ paddingLeft: 5 }}
            />
          </Form.Item>
          <PDFDesignerLinkPicker
            link={link}
            sections={sections}
            collected={collected}
            onLinkChanged={handleLinkChange}
            style={formStyle}
            disabled={isHeader && useStandardTemplate}
            lockSectionId={lockedRepeatableLayoutSectionId}
          />
          {showHideIfEmptySelector && (
            <Form.Item
              key="hideIfEmptyLink"
              style={formStyle}
              label={(
                <Row gutter={10} align="middle">
                  <Col>Hide this item in PDF if linked field is empty:</Col>
                  <Col>
                    <HoverHelp content={hideIfEmptySelectorHelp} />
                  </Col>
                </Row>
              )}
            >
              <Select
                allowClear
                showSearch
                optionFilterProp="label"
                placeholder={
                  hideIfEmptyFieldOptions.length
                    ? 'Select a field'
                    : 'No fields have Hide in PDF if empty enabled'
                }
                value={hideIfEmptySelectValue}
                onChange={onHideIfEmptyFieldChange}
                options={hideIfEmptyFieldOptions}
                disabled={!hideIfEmptyFieldOptions.length}
                getPopupContainer={getPopupContainer}
                style={{ width: '100%' }}
              />
            </Form.Item>
          )}
          {!useV3 && (
            <PDFDesignerPositionPicker
              drawOptions={drawOptions}
              onDrawOptionChanged={onDrawOptionChanged}
              style={formStyle}
            />
          )}
          <PDFDesignerPagePicker
            page={page}
            numberOfPages={numberOfPages}
            onPageChanged={onPageChanged}
            style={formStyle}
            disabled={isHeader && useStandardTemplate}
          />
          <PDFDesignerColorPicker
            style={formStyle}
            title="Background Color"
            value={backgroundColor}
            onColorChanged={(newColor) => onDrawOptionChanged({ backgroundColor: newColor })}
          />
        </Tabs.TabPane>
        <Tabs.TabPane tab="Font" key="font">
          <PDFDesignerFontPicker
            drawOptions={drawOptions}
            onDrawOptionChanged={onDrawOptionChanged}
            showFontScaleSelector
          />
        </Tabs.TabPane>
        <Tabs.TabPane tab="Border" key="border">
          <PDFDesignerBorderPicker
            drawOptions={drawOptions}
            onDrawOptionChanged={onDrawOptionChanged}
            style={formStyle}
          />
        </Tabs.TabPane>
      </Tabs>
    </Form>
  );
}
